import isEmpty from 'lodash/isEmpty'
import React, { useState } from 'react'
import { ListGroup } from 'react-bootstrap'
import { FeatureFlagChoiceSelect } from './FeatureFlagChoiceSelect'
import { EmptyContent } from '@ztechapps/webui.util'
import type { FeatureFlagKeyEnum } from '@ztechapps/common.contract'
import { FeatureFlagChoiceMapping } from '@ztechapps/common.contract'
import { useFeatureFlag } from '@ztechapps/common.hook'
import { humanize } from '@ztechapps/common.utils'

export type FeatureFlagListProps = {
  /**
   * Optional filter to show only specific feature flags.
   * If not provided, all feature flags from FeatureFlagChoiceMapping will be shown.
   */
  featureKeys?: FeatureFlagKeyEnum[]
}

/**
 * Component that displays a list of feature flags with choice selectors.
 */
export const FeatureFlagList = ({ featureKeys }: FeatureFlagListProps) => {
  // Get all feature flag keys to display
  const keysToDisplay = featureKeys ?? (Object.keys(FeatureFlagChoiceMapping) as readonly FeatureFlagKeyEnum[])

  if (isEmpty(keysToDisplay)) {
    return <EmptyContent message="No features available for configuration." />
  }

  return (
    <ListGroup>
      {keysToDisplay.map((key) => {
        const featureName = humanize(key)

        return (
          <ListGroup.Item key={key}>
            <FeatureFlagItem featureKey={key} featureName={featureName} />
          </ListGroup.Item>
        )
      })}
    </ListGroup>
  )
}

// Individual feature flag item component that uses the hook
const FeatureFlagItem = ({ featureKey, featureName }: { featureKey: FeatureFlagKeyEnum; featureName: string }) => {
  const { selectedChoice, setChoice, loading, choices, metadata } = useFeatureFlag(featureKey)
  const [isUpdating, setIsUpdating] = useState(false)

  const handleChoiceChange = async (choice: string) => {
    // Validate the choice is one of the valid options before casting
    if (!choices.includes(choice as (typeof choices)[number])) {
      console.warn(`Invalid choice: ${choice}`)
      return
    }
    setIsUpdating(true)
    try {
      await setChoice(choice as (typeof choices)[number])
    } finally {
      setIsUpdating(false)
    }
  }

  return (
    <FeatureFlagChoiceSelect
      featureKey={featureKey}
      featureName={featureName}
      selectedChoice={selectedChoice}
      choices={choices}
      metadata={metadata}
      onChoiceChange={handleChoiceChange}
      loading={isUpdating || loading}
    />
  )
}
